import type { ReceiptItemAssignment } from '@pet/shared';
import type { IngestionStatusResponse, ReceiptLineItem } from './receiptClient';
import { parsePercent, pickRemainderLine } from './allocation';

// AD-18 Build C: tapping a receipt's extracted items onto the split lines. This module turns item
// assignments into each line's (and the personal bucket's) before-tax cents; allocate() in
// './allocation' then shares the extracted tax pro-rata exactly as it does for typed amounts.
//
// All money is integer cents and every percentage a whole number. An item's share is an integer
// product divided by an integer and floored (Math.floor(a * p / 100)), as in allocation.ts; the odd
// cents go by allocation.ts's own remainder rule (pickRemainderLine), never to the personal bucket.

// E3. Separate from the wizard's advisory banner threshold, although the value is the same.
export const ITEMS_GATE_MIN_CONFIDENCE = 0.8;

// The "Not for the properties" target, and the chip that opens a percentage split.
export const PERSONAL = 'personal';
export const SPLIT_CHOICE = 'split';

// E3: items are tappable only when the extraction is confident and its arithmetic closes. A printed
// subtotal may be before the discounts (the non-negative items) or after them (all the items).
export function receiptItemsGateOpen(
  extraction: Pick<
    IngestionStatusResponse,
    'status' | 'confidence' | 'lineItems' | 'totalCents' | 'subtotalCents' | 'taxCents'
  >
): boolean {
  const { confidence, lineItems, totalCents, subtotalCents, taxCents } = extraction;
  if (extraction.status !== 'AI_COMPLETE') return false;
  if (typeof confidence !== 'number' || confidence < ITEMS_GATE_MIN_CONFIDENCE) return false;
  if (!lineItems || lineItems.length < 1) return false;
  if (typeof taxCents !== 'number' || typeof totalCents !== 'number') return false;
  const itemsSum = lineItems.reduce((s, i) => s + i.amountCents, 0);
  if (itemsSum + taxCents !== totalCents) return false;
  const beforeDiscounts = lineItems.reduce((s, i) => s + Math.max(i.amountCents, 0), 0);
  if (typeof subtotalCents === 'number' && subtotalCents !== itemsSum && subtotalCents !== beforeDiscounts) return false;
  return true;
}

// Where one item goes: a line key, PERSONAL, or a split by whole percentages.
export interface ItemShare {
  target: string; // a line key or PERSONAL
  percent: number;
}

// The wizard's state for one item. `choice` is '' (not assigned yet), a line key, PERSONAL or
// SPLIT_CHOICE; `splitText` holds the typed percentage per target while splitting.
export interface ItemDraft {
  choice: string;
  splitText: Record<string, string>;
}

export function emptyItemDraft(): ItemDraft {
  return { choice: '', splitText: {} };
}

type ItemState = { status: 'assigned'; shares: ItemShare[] } | { status: 'unassigned' } | { status: 'incomplete-split' };

// A draft against the current lines. A line that no longer exists is no target, so removing a
// line leaves its items unassigned. A split counts only when every entry is a whole number and the
// entries sum to 100; blank and 0 mean no share.
export function itemState(draft: ItemDraft, lineIds: string[]): ItemState {
  if (draft.choice === SPLIT_CHOICE) {
    const shares: ItemShare[] = [];
    for (const target of [...lineIds, PERSONAL]) {
      const text = draft.splitText[target] ?? '';
      if (text.trim() === '') continue;
      const percent = parsePercent(text);
      if (percent === null) return { status: 'incomplete-split' };
      if (percent > 0) shares.push({ target, percent });
    }
    const sum = shares.reduce((s, x) => s + x.percent, 0);
    return sum === 100 ? { status: 'assigned', shares } : { status: 'incomplete-split' };
  }
  if (draft.choice === PERSONAL || lineIds.includes(draft.choice)) {
    return { status: 'assigned', shares: [{ target: draft.choice, percent: 100 }] };
  }
  return { status: 'unassigned' };
}

export type ItemBlockReason = 'items-unassigned' | 'item-split-incomplete' | 'personal-below-zero';

export interface ItemAllocation {
  lineCents: Record<string, number>; // before tax, per line key; 0 for a line with no items
  personalCents: number; // before tax
  shares: (ItemShare[] | null)[]; // per item; null = not (completely) assigned
  unassigned: number[]; // item indexes
  incompleteSplits: number[]; // item indexes
  blocked: ItemBlockReason[];
}

export function allocateItems(input: {
  items: ReceiptLineItem[];
  drafts: ItemDraft[]; // aligned with items
  lines: { id: string; propertyAddress: string }[]; // screen order
}): ItemAllocation {
  const lineIds = input.lines.map((l) => l.id);
  const lineCents: Record<string, number> = Object.fromEntries(lineIds.map((id) => [id, 0]));
  let personalCents = 0;
  const shares: (ItemShare[] | null)[] = [];
  const unassigned: number[] = [];
  const incompleteSplits: number[] = [];

  input.items.forEach((item, index) => {
    const state = itemState(input.drafts[index] ?? emptyItemDraft(), lineIds);
    if (state.status !== 'assigned') {
      shares.push(null);
      (state.status === 'unassigned' ? unassigned : incompleteSplits).push(index);
      return;
    }
    shares.push(state.shares);

    const parts = state.shares.map((s) => ({ ...s, cents: Math.floor((item.amountCents * s.percent) / 100) }));
    // floor(x) <= x for every sign, so the remainder is 0 or a few cents, never negative.
    const remainder = item.amountCents - parts.reduce((s, p) => s + p.cents, 0);
    if (remainder > 0) {
      const winner = pickRemainderLine(
        parts
          .filter((p) => p.target !== PERSONAL)
          .map((p) => ({
            id: p.target,
            propertyAddress: input.lines.find((l) => l.id === p.target)?.propertyAddress ?? '',
            preRemainderCents: p.cents,
          }))
      );
      const part = parts.find((p) => p.target === winner);
      if (part) part.cents += remainder;
    }
    for (const p of parts) {
      if (p.target === PERSONAL) personalCents += p.cents;
      else lineCents[p.target] += p.cents;
    }
  });

  const blocked: ItemBlockReason[] = [];
  if (unassigned.length > 0) blocked.push('items-unassigned');
  if (incompleteSplits.length > 0) blocked.push('item-split-incomplete');
  if (personalCents < 0) blocked.push('personal-below-zero');

  return { lineCents, personalCents, shares, unassigned, incompleteSplits, blocked };
}

// Plain-language text for the item reasons, one per incomplete split.
export function itemBlockReasonTexts(result: ItemAllocation): string[] {
  const texts: string[] = [];
  if (result.blocked.includes('items-unassigned')) texts.push('Some receipt items are not assigned yet.');
  for (const index of result.incompleteSplits) texts.push(`Item ${index + 1}'s split must add up to 100%.`);
  if (result.blocked.includes('personal-below-zero')) {
    texts.push("The part not for the properties can't be below $0.00.");
  }
  return texts;
}

// The payload's receiptItemAllocation: line targets become indexes in payload (screen) order, and
// each item's description and cents are sent exactly as the GET response returned them.
export function buildReceiptItemAllocation(
  items: ReceiptLineItem[],
  shares: ItemShare[][],
  lineIds: string[]
): ReceiptItemAssignment[] {
  return items.map((item, index) => ({
    index,
    description: item.description,
    amountCents: item.amountCents,
    shares: shares[index].map((s) => ({
      target: s.target === PERSONAL ? 'personal' : lineIds.indexOf(s.target),
      percent: s.percent,
    })),
  }));
}

// The description prefill: the descriptions of the items on one line (any share of them), or of
// every item when lineId is null — in item order, trimmed, blanks skipped.
export function itemsDescription(
  items: ReceiptLineItem[],
  shares: (ItemShare[] | null)[] | null,
  lineId: string | null
): string {
  return items
    .filter((_, i) => lineId === null || (shares?.[i] ?? []).some((s) => s.target === lineId))
    .map((item) => item.description.trim())
    .filter((d) => d !== '')
    .join(', ');
}
